import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../../lib/utils';
import { mediumText, primaryText, secondaryText, semiBoldText } from '../../../../../styling/stylingHelper';
import { FrontendStaffUser } from '../../../../../services/staff/staffService';
import { hasMainPageRestriction } from '../../../../../services/accessManagement/mainPageRestriction';
import Toggle from '../../../../ui/Toggle';
import { SectionHeading, sectionPanelClass } from './sectionStyles';

interface StaffEmailSettingsSectionProps {
  user: FrontendStaffUser;
  onPriceAlertToggle: (enabled: boolean) => void;
  onWeeklyReportsToggle: (enabled: boolean) => void;
  onStaffSignupEmailToggle?: (enabled: boolean) => void;
}

const StaffEmailSettingsSection: React.FC<StaffEmailSettingsSectionProps> = ({
  user,
  onPriceAlertToggle,
  onWeeklyReportsToggle,
  onStaffSignupEmailToggle,
}) => {
  const { t } = useTranslation();

  const permissions = user.permissions || [];
  // Only staff who can manage other staff have any use for signup notifications.
  const showStaffSignupEmails =
    (permissions.includes('manage_staff') || permissions.includes('all_permissions')) &&
    Boolean(onStaffSignupEmailToggle);

  const titleClass = cn('text-[14px] sm:text-[16px] leading-6 mb-1', semiBoldText, primaryText);
  const descriptionClass = cn(secondaryText, mediumText, 'text-[14px] leading-5 sm:leading-6');

  return (
    <section>
      <SectionHeading>{t('staff.emailSettings')}</SectionHeading>
      {hasMainPageRestriction({ role: 'staff', permissions }) && (
        <p className={cn(secondaryText, 'mt-3 text-sm')}>{t('staff.restrictedReportDelivery')}</p>
      )}
      <div className={cn(sectionPanelClass, 'mt-3 sm:mt-4 divide-y divide-borderGrey dark:divide-borderGrey-dark')}>
        <div className="flex items-center justify-between gap-3 p-3">
          <div className="min-w-0">
            <p className={titleClass}>{t('staff.priceAlertEmails')}</p>
            <p className={descriptionClass}>{t('staff.priceAlertEmailsDescription')}</p>
          </div>
          <Toggle
            checked={user.receive_price_alert_emails ?? true}
            onChange={onPriceAlertToggle}
            ariaLabel={t('staff.priceAlertEmails')}
          />
        </div>

        <div className="flex items-center justify-between gap-3 p-3">
          <div className="min-w-0">
            <p className={titleClass}>{t('staff.weeklyReportsEmails')}</p>
            <p className={descriptionClass}>{t('staff.weeklyReportsEmailsDescription')}</p>
          </div>
          <Toggle
            checked={user.receive_weekly_reports_emails ?? false}
            onChange={onWeeklyReportsToggle}
            ariaLabel={t('staff.weeklyReportsEmails')}
          />
        </div>

        {showStaffSignupEmails && onStaffSignupEmailToggle && (
          <div className="flex items-center justify-between gap-3 p-3">
            <div className="min-w-0">
              <p className={titleClass}>{t('staff.staffSignupEmails')}</p>
              <p className={descriptionClass}>{t('staff.staffSignupEmailsDescription')}</p>
            </div>
            <Toggle
              checked={user.receive_staff_signup_emails ?? false}
              onChange={onStaffSignupEmailToggle}
              ariaLabel={t('staff.staffSignupEmails')}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default StaffEmailSettingsSection;
